import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Grid3x3, ArrowRight } from 'lucide-react';
import { FEATURED_CATEGORIES } from '../data/categories';
import CategoryBrowser from './CategoryBrowser';

/**
 * FeaturedCategories — premium black/gold grid of the 12 most popular
 * NoFee categories. Replaces aggressive horizontal scrolls with a clean,
 * touch-friendly responsive grid.
 */
const FeaturedCategories = ({ title = 'Browse by category', subtitle = 'Find what you crave', className = '' }) => {
  const [browseOpen, setBrowseOpen] = useState(false);

  return (
    <section className={`relative ${className}`} data-testid="featured-categories">
      <div className="flex items-end justify-between mb-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-gold-400 font-bold">
            {subtitle}
          </p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-1">
            {title}
          </h2>
        </div>
        <button
          onClick={() => setBrowseOpen(true)}
          className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-gold-300 hover:text-gold-200 font-semibold transition-colors"
          data-testid="view-all-categories-top"
        >
          View all
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Grid — 3 cols mobile / 4 cols sm / 6 cols lg */}
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3">
        {FEATURED_CATEGORIES.map((c) => (
          <Link
            key={c.id}
            to={`/category/${c.id}`}
            data-testid={`featured-category-${c.id}`}
            className="group flex flex-col items-center justify-center gap-2 px-2 py-5 sm:py-6 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-gold-500/40 transition-all duration-300 hover:-translate-y-0.5"
          >
            <span className="text-3xl sm:text-4xl transition-transform group-hover:scale-110">
              {c.emoji}
            </span>
            <span className="text-[12px] sm:text-[13px] font-bold text-white/85 group-hover:text-gold-200 text-center">
              {c.label}
            </span>
          </Link>
        ))}

        {/* "View All" tile */}
        <button
          onClick={() => setBrowseOpen(true)}
          data-testid="view-all-categories-tile"
          className="group flex flex-col items-center justify-center gap-2 px-2 py-5 sm:py-6 rounded-2xl border border-dashed border-gold-500/30 bg-gold-500/[0.04] hover:bg-gold-500/[0.08] hover:border-gold-500/60 transition-all duration-300 hover:-translate-y-0.5 col-span-3 sm:col-span-1"
        >
          <Grid3x3 className="w-7 h-7 text-gold-300 transition-transform group-hover:rotate-90 duration-300" />
          <span className="text-[12px] sm:text-[13px] font-bold text-gold-200">
            View All
          </span>
        </button>
      </div>

      <CategoryBrowser open={browseOpen} onClose={() => setBrowseOpen(false)} />
    </section>
  );
};

export default FeaturedCategories;
